import type { NavigateFunction } from 'react-router-dom'

export type ProfileTab =
  | 'resumen'
  | 'servicios'
  | 'oportunidades'
  | 'calificaciones'
  | 'muro'
  | 'gestion'

export type ProfileGestionSection =
  | 'cotizaciones'
  | 'accesos'
  | 'documentaciones'
  | 'proyectos'

export const PROFILE_GESTION_SECTIONS: ProfileGestionSection[] = [
  'cotizaciones',
  'accesos',
  'documentaciones',
  'proyectos',
]

export const GESTION_SECTION_QUERY_TO_LABEL: Record<
  ProfileGestionSection,
  string
> = {
  cotizaciones: 'Cotización de materiales',
  accesos: 'Accesos a obras',
  documentaciones: 'Documentaciones de obra',
  proyectos: 'Proyectos publicados',
}

export function withConversationState(
  navigate: NavigateFunction
): NavigateFunction {
  return ((to: any, options?: any) => {
    const state =
      options?.state && typeof options.state === 'object' ? options.state : {}
    navigate(to, {
      ...options,
      state: { ...state, fromConversation: true },
    })
  }) as NavigateFunction
}

export const TAB_TO_QUERY_PARAM: Record<string, ProfileTab> = {
  Resumen: 'resumen',
  'Productos/Servicios': 'servicios',
  Servicios: 'servicios',
  Oportunidades: 'oportunidades',
  Calificaciones: 'calificaciones',
  Muro: 'muro',
  'Gestión': 'gestion',
}

export const QUERY_PARAM_TO_TAB: Record<ProfileTab, string> = {
  resumen: 'Resumen',
  servicios: 'Productos/Servicios',
  oportunidades: 'Oportunidades',
  calificaciones: 'Calificaciones',
  muro: 'Muro',
  gestion: 'Gestión',
}

export const PROFILE_SUBITEM_TAB_MAPPING: Record<string, string> = {
  'mis-proyectos': 'Oportunidades',
  'mis-cotizaciones-materiales': 'Gestión',
  'mis-documentaciones': 'Gestión',
  'mis-productos-servicios': 'Productos/Servicios',
  'mis-postulaciones': 'Gestión',
}

export const PROFILE_SUBITEM_GESTION_SECTION: Partial<
  Record<string, ProfileGestionSection>
> = {
  'mis-cotizaciones-materiales': 'cotizaciones',
  'mis-documentaciones': 'documentaciones',
  'mis-postulaciones': 'accesos',
}

export const PROFILE_TAB_TO_NAV_ITEM: Record<string, string> = {
  Oportunidades: 'mis-contenidos',
  Gestión: 'mis-contenidos',
  'Productos/Servicios': 'mis-contenidos',
  Muro: 'mis-contenidos',
}

export interface BuildProfileUrlOptions {
  section?: ProfileGestionSection | null
}

export const resolveProfileTab = (input?: string | null): ProfileTab | null => {
  if (!input) return null
  const value = input.trim()
  const direct = TAB_TO_QUERY_PARAM[value]
  if (direct) return direct
  if (Object.prototype.hasOwnProperty.call(QUERY_PARAM_TO_TAB, value)) {
    return value as ProfileTab
  }
  const lower = value.toLowerCase()
  if (Object.prototype.hasOwnProperty.call(QUERY_PARAM_TO_TAB, lower)) {
    return lower as ProfileTab
  }
  return null
}

export const resolveGestionSection = (
  input?: string | null
): ProfileGestionSection | null => {
  if (!input) return null
  const v = input.trim().toLowerCase()
  for (const s of PROFILE_GESTION_SECTIONS) {
    if (s === v) return s
  }
  return null
}

export const getGestionSectionFromQuery = (
  searchParams: URLSearchParams
): ProfileGestionSection | null => {
  return resolveGestionSection(searchParams.get('section'))
}

export function buildProfileUrl(
  userId: string,
  userName?: string | null,
  tab?: string | null,
  options?: BuildProfileUrlOptions
): string {
  const baseUrl = `/perfil/${userId}`
  const search = new URLSearchParams()
  const resolvedTab = resolveProfileTab(tab)
  if (resolvedTab) {
    search.set('tab', resolvedTab)
  }
  if (resolvedTab === 'gestion' && options?.section) {
    search.set('section', options.section)
  }
  if (userName) {
    search.set('userName', userName)
  }
  const query = search.toString()
  return query ? `${baseUrl}?${query}` : baseUrl
}

export function buildProfileUrlFromSubitem(
  userId: string,
  userName: string | null | undefined,
  subItemId: string
): string {
  const tabLabel = PROFILE_SUBITEM_TAB_MAPPING[subItemId]
  if (!tabLabel) {
    return buildProfileUrl(userId, userName)
  }
  const section = PROFILE_SUBITEM_GESTION_SECTION[subItemId]
  return buildProfileUrl(userId, userName, tabLabel, {
    section: section ?? undefined,
  })
}

export const getUserDisplayName = (user: any): string | null => {
  if (!user) return null
  if (user.type === 'COMPANY') {
    return user.razon_social || user.business_name || null
  }
  const nameParts = [user.name, user.last_name].filter(Boolean)
  if (!nameParts.length) return null
  const displayName = nameParts.join(' ').trim()
  return displayName || null
}

export interface ProfilePathInfo {
  isProfile: boolean
  userId: string | null
  userName: string | null
}

export const getProfilePathInfo = (pathname: string): ProfilePathInfo => {
  const segments = pathname.split('/').filter(Boolean)
  const first = segments[0]
  const isProfileRoute = first === 'profesionales' || first === 'perfil'
  if (!isProfileRoute) {
    return {
      isProfile: false,
      userId: null,
      userName: null,
    }
  }

  const userId = segments[1] || null
  const rawName = segments.length > 2 ? segments.slice(2).join('/') : null
  const userName = rawName ? decodeURIComponent(rawName) : null

  return {
    isProfile: Boolean(userId),
    userId,
    userName,
  }
}

export interface NavigationFromProfile {
  from: 'perfil'
  tab: ProfileTab
  userId: string
  userName: string
  section?: ProfileGestionSection | null
}

export const buildNavigationParams = (
  tab: string,
  userId: string,
  userName: string,
  gestionSection?: ProfileGestionSection | null
): string => {
  const search = new URLSearchParams()
  search.set('from', 'perfil')
  const resolvedTab = resolveProfileTab(tab) || (tab as ProfileTab)
  search.set('tab', resolvedTab)
  search.set('userId', userId)
  search.set('userName', userName)
  if (resolvedTab === 'gestion' && gestionSection) {
    search.set('section', gestionSection)
  }
  const query = search.toString()
  return query ? `?${query}` : ''
}

export const parseNavigationParams = (
  searchParams: URLSearchParams
): NavigationFromProfile | null => {
  const from = searchParams.get('from')
  const rawTab = searchParams.get('tab')
  const tab = resolveProfileTab(rawTab)
  const userId = searchParams.get('userId')
  const userName = searchParams.get('userName')
  const section = getGestionSectionFromQuery(searchParams)

  if (from === 'perfil' && tab && userId && userName) {
    let resolvedName = userName
    try {
      resolvedName = decodeURIComponent(userName)
    } catch {
      resolvedName = userName
    }
    return {
      from: 'perfil',
      tab,
      userId,
      userName: resolvedName,
      section: tab === 'gestion' ? section : null,
    }
  }

  return null
}

export const getProfileTabFromQuery = (
  searchParams: URLSearchParams
): string | null => {
  const rawTab = searchParams.get('tab')
  const tabParam = resolveProfileTab(rawTab)
  if (tabParam && QUERY_PARAM_TO_TAB[tabParam]) {
    return QUERY_PARAM_TO_TAB[tabParam]
  }
  return null
}

export interface ProfessionalsListNavigationParams {
  from: 'profesionales'
  skillId?: number
  jurisdictionIds: number[]
  allCountry: boolean
  page?: number
}

export const parseProfessionalsListNavigationParams = (
  searchParams: URLSearchParams
): ProfessionalsListNavigationParams | null => {
  const from = searchParams.get('from')
  if (from !== 'profesionales') {
    return null
  }

  const skillParam = searchParams.get('skill')
  const jurisdictionsParam = searchParams.get('jurisdictions')
  const allCountryParam = searchParams.get('allCountry')
  const pageParam = searchParams.get('page')

  const skillId = skillParam ? Number(skillParam) : undefined
  const jurisdictionIds = jurisdictionsParam
    ? jurisdictionsParam
        .split(',')
        .map((value) => Number(value))
        .filter((value) => !Number.isNaN(value))
    : []
  const allCountry = allCountryParam === '1'
  const page = pageParam ? Number(pageParam) : undefined

  return {
    from: 'profesionales',
    skillId,
    jurisdictionIds,
    allCountry,
    page,
  }
}

export const buildProfessionalsListUrl = (params: {
  skillId?: number
  jurisdictionIds?: number[]
  allCountry?: boolean
  page?: number
}) => {
  const search = new URLSearchParams()
  if (typeof params.skillId === 'number') {
    search.set('skill', String(params.skillId))
  }
  if (params.jurisdictionIds && params.jurisdictionIds.length > 0) {
    search.set('jurisdictions', params.jurisdictionIds.join(','))
  }
  if (params.allCountry) {
    search.set('allCountry', '1')
  }
  if (params.page && params.page > 1) {
    search.set('page', String(params.page))
  }
  const queryString = search.toString()
  return `/buscar-perfiles${queryString ? `?${queryString}` : ''}`
}

export const getProfessionalsFiltersFromSearch = (
  searchParams: URLSearchParams
) => {
  const skillParam = searchParams.get('skill')
  const jurisdictionsParam = searchParams.get('jurisdictions')
  const allCountryParam = searchParams.get('allCountry')
  const pageParam = searchParams.get('page')

  if (!skillParam && !jurisdictionsParam && !allCountryParam && !pageParam) {
    return null
  }

  const skillId = skillParam ? Number(skillParam) : undefined
  const jurisdictionIds = jurisdictionsParam
    ? jurisdictionsParam
        .split(',')
        .map((value) => Number(value))
        .filter((value) => !Number.isNaN(value))
    : []
  const allCountry = allCountryParam === '1'
  const page = pageParam ? Number(pageParam) : undefined

  return {
    skillId,
    jurisdictionIds,
    allCountry,
    page,
  }
}
